import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchLatestIssue, fetchRecentPapers } from '../utils/api';

const Home = () => {
  const [latestIssue, setLatestIssue] = useState(null);
  const [recentPapers, setRecentPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({
    totalArticles: 0,
    citationIndex: 0,
    countries: 0,
    impactFactor: 0
  });
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  
  const targetStats = {
    totalArticles: 1247,
    citationIndex: 2.8,
    countries: 45,
    impactFactor: 3.2
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const issueData = await fetchLatestIssue();
        const papersData = await fetchRecentPapers();
        
        setLatestIssue(issueData);
        setRecentPapers(papersData);
      } catch (err) {
        console.error('Error loading home page data:', err);
        setError('Failed to load journal data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    
    // Add scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          
          // Animate stats when visible
          if (entry.target === statsRef.current && !statsVisible) {
            setStatsVisible(true);
            animateStats();
          }
        }
      });
    }, observerOptions);
    
    // Observe all fade-in elements
    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  const animateStats = useCallback(() => {
    const duration = 2000;
    const startTime = Date.now();
    
    const updateStats = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      setAnimatedStats({
        totalArticles: Math.floor(targetStats.totalArticles * easeOutQuart),
        citationIndex: (targetStats.citationIndex * easeOutQuart).toFixed(1),
        countries: Math.floor(targetStats.countries * easeOutQuart),
        impactFactor: (targetStats.impactFactor * easeOutQuart).toFixed(1)
      });
      
      if (progress < 1) {
        requestAnimationFrame(updateStats);
      }
    };
    
    requestAnimationFrame(updateStats);
  }, []);

  // Using real data from API
  const displayArticles = recentPapers;

  const features = [
    {
      icon: "fas fa-unlock-alt",
      title: "Open Access",
      description: "All research is freely accessible worldwide, ensuring knowledge reaches every corner of the globe without barriers."
    },
    {
      icon: "fas fa-users",
      title: "Rigorous Peer Review",
      description: "Every submission undergoes thorough evaluation by leading experts in agriculture and life sciences."
    },
    {
      icon: "fas fa-clock",
      title: "Fast Publication",
      description: "Streamlined review process ensures rapid dissemination of important research findings to the academic community."
    },
    {
      icon: "fas fa-globe-africa",
      title: "Global Reach",
      description: "Contributing to international scientific discourse with research that addresses global agricultural challenges."
    }
  ];

  return (
    <div className="main-content">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Federal University Wukari Journal of Agriculture and Life Sciences</h1>
            <p className="hero-subtitle">
              Pioneering research excellence in agriculture and life sciences, fostering innovation 
              for sustainable food security and environmental stewardship across Africa and beyond.
            </p>
            <div className="hero-buttons">
              <Link to="/submit-paper" className="btn btn-white">
                <i className="fas fa-paper-plane"></i>
                Submit Your Research
              </Link>
              <Link to="/issues" className="btn btn-outline" style={{borderColor: 'white', color: 'white'}}>
                <i className="fas fa-book-open"></i>
                Explore Publications
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Publish With FUWJALS?</h2>
            <p className="section-subtitle">
              Join a prestigious community of researchers committed to advancing knowledge 
              in agriculture and life sciences through rigorous scholarship.
            </p>
          </div>
          <div className="feature-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card fade-in" style={{'--card-index': index}}>
                <div className="feature-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Publications Section */}
      <section className="section bg-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Latest Publications</h2>
            <p className="section-subtitle">
              Discover groundbreaking research that's shaping the future of agriculture and life sciences
            </p>
          </div>
          
          {loading ? (
            <div className="text-center">
              <div className="spinner"></div>
              <p className="mt-3">Loading latest research...</p>
            </div>
          ) : error && recentPapers.length === 0 ? (
            <div className="article-grid">
              {displayArticles.map((article, index) => (
                <div key={article.id} className="article-card fade-in" style={{'--card-index': index}}>
                  <h3 className="article-title">{article.title}</h3>
                  <div className="article-authors">
                    By {Array.isArray(article.authors) && article.authors.length > 0
                      ? article.authors.map(a => typeof a === 'object' ? a.name : a).join(', ')
                      : (article.authors || 'Unknown')}
                  </div>
                  <p className="article-abstract">{article.abstract}</p>
                  <div className="article-keywords">
                    {article.keywords && (
                      typeof article.keywords === 'string'
                        ? article.keywords.split(',').map((keyword, index) => (
                            <span key={index} className="keyword-tag">{keyword.trim()}</span>
                          ))
                        : Array.isArray(article.keywords)
                          ? article.keywords.map((keyword, index) => (
                              <span key={index} className="keyword-tag">{keyword}</span>
                            ))
                          : null
                    )}
                  </div>
                  <div className="article-footer">
                    <div className="article-date">
                      <i className="fas fa-calendar-alt"></i>
                      Published: {(article.publishedDate || article.submittedDate || article.createdAt)
                        ? new Date(article.publishedDate || article.submittedDate || article.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Date not available'}
                    </div>
                    <div className="article-actions">
                      <Link
                        to={`/paper/${article.id}`}
                        state={{ from: '/', fromLabel: 'Home' }}
                        className="btn btn-primary"
                      >
                        <i className="fas fa-eye"></i>
                        Read Article
                      </Link>
                    </div>
                  </div>
                  <div className="card-meta">
                    <div className="card-meta-item">
                      <i className="fas fa-download"></i>
                      {article.downloadCount || 0} downloads
                    </div>
                    <div className="card-meta-item">
                      <i className="fas fa-quote-right"></i>
                      {article.citations || 0} citations
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="article-grid">
              {displayArticles.slice(0, 6).map((article, index) => (
                <div key={article.id} className="article-card fade-in" style={{'--card-index': index}}>
                  <h3 className="article-title">{article.title}</h3>
                  <div className="article-authors">
                    By {Array.isArray(article.authors) && article.authors.length > 0
                      ? article.authors.map(a => typeof a === 'object' ? a.name : a).join(', ')
                      : (article.authors || 'Unknown')}
                  </div>
                  <p className="article-abstract">{article.abstract}</p>
                  <div className="article-keywords">
                    {article.keywords && (
                      typeof article.keywords === 'string'
                        ? article.keywords.split(',').map((keyword, index) => (
                            <span key={index} className="keyword-tag">{keyword.trim()}</span>
                          ))
                        : Array.isArray(article.keywords)
                          ? article.keywords.map((keyword, index) => (
                              <span key={index} className="keyword-tag">{keyword}</span>
                            ))
                          : null
                    )}
                  </div>
                  <div className="article-footer">
                    <div className="article-date">
                      <i className="fas fa-calendar-alt"></i>
                      Published: {(article.publishedDate || article.submittedDate || article.createdAt)
                        ? new Date(article.publishedDate || article.submittedDate || article.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Date not available'}
                    </div>
                    <div className="article-actions">
                      <Link
                        to={`/paper/${article.id}`}
                        state={{ from: '/', fromLabel: 'Home' }}
                        className="btn btn-primary"
                      >
                        <i className="fas fa-eye"></i>
                        Read Article
                      </Link>
                    </div>
                  </div>
                  <div className="card-meta">
                    <div className="card-meta-item">
                      <i className="fas fa-download"></i>
                      {article.downloadCount || 0} downloads
                    </div>
                    <div className="card-meta-item">
                      <i className="fas fa-quote-right"></i>
                      {article.citations || 0} citations
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-5">
            <Link to="/current-publications" className="btn btn-outline-primary btn-lg">
              <i className="fas fa-th-list me-2"></i>
              View More Featured Articles
            </Link>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" style={{color: 'white'}}>Journal Impact & Reach</h2>
            <p className="section-subtitle" style={{color: 'rgba(255,255,255,0.9)'}}>
              Measuring our contribution to the global scientific community
            </p>
          </div>
          <div className="stats-grid">
            <div className="stat-item" style={{'--stat-index': 0}}>
              <div className="stat-number">{animatedStats.totalArticles.toLocaleString()}</div>
              <div className="stat-label">Published Articles</div>
            </div>
            <div className="stat-item" style={{'--stat-index': 1}}>
              <div className="stat-number">{animatedStats.impactFactor}</div>
              <div className="stat-label">Impact Factor</div>
            </div>
            <div className="stat-item" style={{'--stat-index': 2}}>
              <div className="stat-number">{animatedStats.countries}+</div>
              <div className="stat-label">Countries Reached</div>
            </div>
            <div className="stat-item" style={{'--stat-index': 3}}>
              <div className="stat-number">{animatedStats.citationIndex}</div>
              <div className="stat-label">Citation Index</div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Issue Highlight */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Current Issue Highlight</h2>
          </div>
          
          <div className="card fade-in" style={{maxWidth: '800px', margin: '0 auto'}}>
            <div className="card-body text-center">
              {loading ? (
                <div className="py-5">
                  <div className="spinner"></div>
                  <p className="mt-3">Loading current issue...</p>
                </div>
              ) : latestIssue ? (
                <>
                  <h3 className="card-title">{latestIssue.title}</h3>
                  <p className="text-secondary mb-3">
                    Volume {latestIssue.volume}, Issue {latestIssue.issue} • {latestIssue.publicationDate}
                  </p>
                  <p className="card-text">{latestIssue.description}</p>
                  <Link to={`/issues/${latestIssue.id}`} className="btn btn-primary mt-3">
                    <i className="fas fa-journal-whills"></i>
                    Explore This Issue
                  </Link>
                </>
              ) : (
                <>
                  <h3 className="card-title">Call for Special Issues</h3>
                  <p className="text-secondary mb-3">Submit your proposals</p>
                  <p className="card-text">
                    This special issue focuses on innovative agricultural practices and technologies 
                    that promote sustainability while addressing food security challenges across the African continent.
                  </p>
                  <Link to="/issues" className="btn btn-primary mt-3">
                    <i className="fas fa-journal-whills"></i>
                    Explore All Issues
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action for Authors */}
      <section className="section bg-light">
        <div className="container">
          <div className="text-center">
            <h2 className="section-title">Ready to Share Your Research?</h2>
            <p className="section-subtitle mb-5">
              Join our community of distinguished researchers and contribute to the advancement 
              of agriculture and life sciences. Your research can make a difference.
            </p>
            
            <div className="feature-grid" style={{maxWidth: '600px', margin: '0 auto 3rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'}}>
              <div className="text-center fade-in" style={{'--card-index': 0}}>
                <div className="feature-icon" style={{width: '60px', height: '60px', fontSize: '1.5rem', margin: '0 auto 1rem'}}>
                  <i className="fas fa-upload"></i>
                </div>
                <h4>Submit Online</h4>
                <p>Easy online submission system</p>
              </div>
              <div className="text-center fade-in" style={{'--card-index': 1}}>
                <div className="feature-icon" style={{width: '60px', height: '60px', fontSize: '1.5rem', margin: '0 auto 1rem'}}>
                  <i className="fas fa-user-check"></i>
                </div>
                <h4>Expert Review</h4>
                <p>Rigorous peer review process</p>
              </div>
              <div className="text-center fade-in" style={{'--card-index': 2}}>
                <div className="feature-icon" style={{width: '60px', height: '60px', fontSize: '1.5rem', margin: '0 auto 1rem'}}>
                  <i className="fas fa-globe"></i>
                </div>
                <h4>Global Impact</h4>
                <p>Worldwide accessibility</p>
              </div>
            </div>

            <div className="hero-buttons">
              <Link to="/submit-paper" className="btn btn-primary">
                <i className="fas fa-paper-plane"></i>
                Submit Your Manuscript
              </Link>
              <Link to="/author-guidelines" className="btn btn-outline">
                <i className="fas fa-file-alt"></i>
                Author Guidelines
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
